import { DateQuickLinkAddControl } from "@/Common/Components/Buttons/DateQuickLinkAddControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { Comment } from "@/Common/Models/Patient/Comment";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { MedicalRequestStatus } from "@/Portfolio/Models/MedicalRequestStatus";
import { MedicalRequestCommentsDisplay } from "./MedicalRequestCommentsDisplay";
import { MedicalRequestFormRecipientField } from "./MedicalRequestFormRecipientField";
import { MedicalRequestFormRowComponent } from "./MedicalRequestFormRow";

import stylesImport from "./MedicalRequestPopupController.module.scss";
import legacyStylesImport from "./MedicalRequestPopupControllerLegacy.module.scss";

interface IMedicalRequestStatusSectionProps {
    model: IMedicalRequest;
    comment: string;
    titleLabel: string;
    disabled?: boolean;
    hideCommentInput?: boolean;

    onCommentChanged: (comment: string) => void;
    onStatusChanged: (statusLookupId: string) => void;
    onDueDateChanged: (dueDate: DateTimeWithPrecision) => void;
    onConsultationDateTimeChange?: (date: Date) => void;

    onRecipientChange: (recipient: string) => void;
    onRecipientSearchTextChange: (searchText: string) => void;
    onContactDirectoryEntityChanged: (selection: DirectorySelectableEntity<IDirectoryEntity>) => void;
    onClearRecipient: () => void;
}

class MedicalRequestStatusSection extends React.Component<IMedicalRequestStatusSectionProps, never> {
    private readonly styles = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
        ? stylesImport
        : legacyStylesImport;

    public render(): JSX.Element {
        const model = this.props.model;
        if (!model)
            return null;

        return (
            <>
                <MedicalRequestFormRowComponent label={cacheStore.GetString(this.props.titleLabel)} dataCy="StatusHeader" className={this.styles.requestStatusHeader} />

                <MedicalRequestFormRowComponent label="RequestStatusLabel" dataCy="StatusRow" className={this.styles.requestStatusRow}>
                    {this.renderSelectableStatus(model.statusLookupId)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="RecipientLabel" dataCy="RecipientRow" className={this.styles.recipientRow} shouldHide={!cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements}>
                    {this.renderRecipient()}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="ResultsExpectedOnLabel" dataCy="DueDateRow" className={this.styles.resultsExpectedOnRow}>
                    {this.renderDueDate(model.dueDate, model.dueDateText)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="ConsultationDateLabel" dataCy="ConsultationDateRow" className={this.styles.consultationDateRow} shouldHide={!(this.isConsultationRequest && cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements)}>
                    {this.renderConsultationDate(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="CommentsLabel" dataCy="CommentsRow" className={this.styles.commentsRow} shouldHide={this.shouldHideComment()}>
                    {this.renderCommentsRow(model.comments)}
                </MedicalRequestFormRowComponent>
            </>
        );
    }

    private renderSelectableStatus(statusLookupId: string): JSX.Element {
        return (
            <div>
                <SelectLookupControl
                    IncludeEmptyChoice={true}
                    invalid={!statusLookupId}
                    Value={statusLookupId}
                    AvailableValues={MedicalRequestStatus.getComboboxItems()}
                    Callback={(value: string) => this.props.onStatusChanged && this.props.onStatusChanged(value)}
                    disabled={this.props.disabled}
                    dataCy="MedicalRequestStatusDropdown"
                />
            </div>
        );
    }

    private renderRecipient(): JSX.Element {
        return (
            <MedicalRequestFormRecipientField
                model={this.props.model}
                disabled={this.props.disabled}
                onRecipientChange={selection => this.props.onRecipientChange?.(selection)}
                onRecipientSearchTextChange={textValue => this.props.onRecipientSearchTextChange?.(textValue)}
                onContactDirectoryEntityChanged={selection => this.props.onContactDirectoryEntityChanged(selection)}
                onClearRecipient={this.props.onClearRecipient}
            />
        );
    }

    private renderCommentsRow(comments: Comment[]): JSX.Element {
        const id = "Comments";
        const label = this.isConsultationRequest ? cacheStore.GetString("RequestHistoryLabel") : "";

        return (
            <>
                {!this.props.hideCommentInput && (
                    <div style={{ marginBottom: "5px" }}>
                        <TextAreaAutoSize
                            id={id}
                            onTextChangeCallback={(value: string) => this.props.onCommentChanged && this.props.onCommentChanged(value)}
                            value={this.props.comment}
                            maxHeight="150px"
                            disabled={this.props.disabled}
                        />
                    </div>
                )}
                <MedicalRequestCommentsDisplay comments={comments} label={label} />
            </>
        );
    }

    private renderDueDate(dueDate: Date, dueDateText: string): JSX.Element {
        return (
            <div className="row">
                <div className="col-xs-6">
                    <TextboxControlWithDatePicker
                        id="labformRequestDueDate"
                        expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                        placeholder="YYYY-MM-DD"
                        textValue={dueDateText}
                        dateValue={dueDate}
                        onChange={(date: DateTimeWithPrecision) => this.props.onDueDateChanged && this.props.onDueDateChanged(date)}
                        fullWidth={true}
                    />
                </div>
                {!this.props.disabled && (
                    <div className="col-xs-6" style={{ paddingTop: "8px" }}>
                        <DateQuickLinkAddControl
                            id="labrequest-date-control"
                            value={dueDate}
                            onClick={(date: DateTimeWithPrecision) => this.props.onDueDateChanged && this.props.onDueDateChanged(date)}
                            numbersToRender={[
                                { value: 1, dateLevel: "week" },
                                { value: 2, dateLevel: "week" },
                                { value: 3, dateLevel: "week" },
                                { value: 1, dateLevel: "month" },
                                { value: 2, dateLevel: "month" },
                                { value: 3, dateLevel: "month" }
                            ]}
                        />
                    </div>
                )}
            </div>
        );
    }

    private shouldHideComment(): boolean {
        return this.props.hideCommentInput
            && isArrayNullOrUndefinedOrEmpty(this.props.model.comments);
    }

    private get isConsultationRequest(): boolean {
        return this.props.model instanceof ConsultationRequest;
    }

    private renderConsultationDate(model: IMedicalRequest): JSX.Element {
        const consultationModel = model as ConsultationRequest;
        const referralConsultationDate = consultationModel.referralConsultationDate ? LuxonDateTimeParser.parse(consultationModel.referralConsultationDate).toJSDate() : null;

        return (
            <div className="row">
                <div className="col-xs-6">
                    <DateSelector
                        id="consultationRequestConsultationDate"
                        date={referralConsultationDate}
                        onChangeCallback={date => this.onConsultationDateTimeChange(date, true)}
                        dateFormat="yyyy-MM-dd"
                        disabled={this.props.disabled}
                    />
                </div>
                <div className="col-xs-6">
                    <TimeSelector
                        id="consultationRequestConsultationTime"
                        date={referralConsultationDate}
                        onChangeCallback={date => this.onConsultationDateTimeChange(date, false)}
                        dateFormat="HH:mm"
                        disabled={this.props.disabled}
                    />
                </div>
            </div>
        );
    }

    private onConsultationDateTimeChange(dateTime: Date, changeDate: boolean): void {
        const model = this.props.model as ConsultationRequest;
        let nextDateTime = new Date(model.referralConsultationDate?.toString() ?? new Date().setHours(0, 0, 0, 0));

        if (changeDate) {
            if (!!dateTime)
                nextDateTime.setFullYear(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());
            else
                nextDateTime = null;
        }
        else {
            nextDateTime.setHours(dateTime?.getHours() ?? 0);
            nextDateTime.setMinutes(dateTime?.getMinutes() ?? 0);
        }

        this.props.onConsultationDateTimeChange(nextDateTime);
    }
}

export { IMedicalRequestStatusSectionProps, MedicalRequestStatusSection };
